import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { SANS } from '../theme/todayTokens';

const AccessibilityContext = createContext();

export const useAccessibility = () => {
  const context = useContext(AccessibilityContext);
  if (!context) {
    throw new Error('useAccessibility must be used within an AccessibilityProvider');
  }
  return context;
};

export const AccessibilityProvider = ({ children }) => {
  const defaultSettings = {
    fontSize: 16,
    lineHeight: 1.5,
    fontFamily: 'system-ui',
    letterSpacing: 0,
    textContrast: 'normal',
    fontWeight: 'normal'
  };

  const [accessibilitySettings, setAccessibilitySettings] = useState(defaultSettings);

  const applyAccessibilitySettings = (settings) => {
    const root = document.documentElement;
    
    // Apply settings to root element for global scope
    root.style.setProperty('--app-font-size', `${settings.fontSize}px`);
    root.style.setProperty('--app-line-height', settings.lineHeight);
    // 'system-ui' is the stored default (saved for every new user), so it maps to the design font; explicit picks are kept.
    root.style.setProperty('--app-font-family', !settings.fontFamily || settings.fontFamily === 'system-ui' ? SANS : settings.fontFamily);
    root.style.setProperty('--app-letter-spacing', `${settings.letterSpacing}px`);
    root.style.setProperty('--app-font-weight', settings.fontWeight);

    // Apply contrast settings
    if (settings.textContrast === 'high') {
      root.style.setProperty('--app-text-opacity', '1');
      root.style.setProperty('--app-contrast-ratio', '1.5');
    } else if (settings.textContrast === 'highest') {
      root.style.setProperty('--app-text-opacity', '1');
      root.style.setProperty('--app-contrast-ratio', '2');
    } else {
      root.style.setProperty('--app-text-opacity', '0.87');
      root.style.setProperty('--app-contrast-ratio', '1');
    }
  };

  const updateAccessibilitySettings = async (newSettings) => {
    // Only update Firestore if user is authenticated
    if (!auth.currentUser) {
      // Apply settings locally without saving to Firestore
      setAccessibilitySettings(newSettings);
      applyAccessibilitySettings(newSettings);
      return false;
    }

    try {
      const settingsRef = doc(db, 'settings', 'global');
      await setDoc(settingsRef, {
        accessibility: newSettings
      }, { merge: true });
      
      setAccessibilitySettings(newSettings);
      applyAccessibilitySettings(newSettings);
      return true;
    } catch (error) {
      console.error('Error updating accessibility settings:', error);
      // Even if the update fails, still apply the settings locally
      setAccessibilitySettings(newSettings);
      applyAccessibilitySettings(newSettings);
      return false;
    }
  };

  // Load saved settings on mount - only if authenticated
  useEffect(() => {
    const loadSettings = async () => {
      // Check if user is authenticated before accessing Firestore
      if (!auth.currentUser) {
        // Apply default settings for unauthenticated users
        applyAccessibilitySettings(defaultSettings);
        return;
      }

      try {
        const settingsRef = doc(db, 'settings', 'global');
        const settingsDoc = await getDoc(settingsRef);
        
        if (settingsDoc.exists() && settingsDoc.data().accessibility) {
          const savedSettings = settingsDoc.data().accessibility;
          setAccessibilitySettings(savedSettings);
          applyAccessibilitySettings(savedSettings);
        } else {
          // If the document doesn't exist, create it with default settings
          await setDoc(settingsRef, {
            accessibility: defaultSettings
          });
          applyAccessibilitySettings(defaultSettings);
        }
      } catch (error) {
        // Only log error if it's not a permission error (expected for unauthenticated users)
        if (error.code !== 'permission-denied') {
          console.error('Error loading accessibility settings:', error);
        }
        // Apply default settings if there's an error
        applyAccessibilitySettings(defaultSettings);
      }
    };

    loadSettings();

    // Listen for auth state changes
    const unsubscribe = auth.onAuthStateChanged(() => {
      loadSettings();
    });

    return () => unsubscribe();
  }, []);

  return (
    <AccessibilityContext.Provider value={{
      accessibilitySettings,
      updateAccessibilitySettings
    }}>
      {children}
    </AccessibilityContext.Provider>
  );
}; 